import { http } from '@/lib/api-client'
import type {
  StudentDetail,
  StudentCounts,
  StudentByCedula,
  CreateStudentInput,
  UpdateStudentInput,
  StudentListResponse,
  StudentExportResponse,
  ListStudentsParams,
  BillingInfo,
  BillingInfoInput,
} from './students.types'

// CRUD for students — /api/students. Branch-scoped by the lesson.
// list → full paginated envelope; single → unwrapped entity.
export const studentApi = {
  list: (params?: ListStudentsParams): Promise<StudentListResponse> =>
    http.get<StudentListResponse>('/students', { params }),

  exportList: (params?: Omit<ListStudentsParams, 'limit' | 'offset'>): Promise<StudentExportResponse> =>
    http.get<StudentExportResponse>('/students/export', { params, timeout: 30_000 }),

  // Facet counts for status and modality badges. Each facet honors the other
  // active filters, so a badge matches the result after clicking it.
  counts: (params?: Omit<ListStudentsParams, 'sortBy' | 'sortDir' | 'limit' | 'offset'>): Promise<StudentCounts> =>
    http.get<{ data: StudentCounts }>('/students/counts', { params }).then((r) => r.data),

  getById: (id: string): Promise<StudentDetail> =>
    http.get<{ data: StudentDetail }>(`/students/${id}`).then((r) => r.data),

  // Existing student with this cédula in the active branch (null if none).
  byCedula: (cedula: string): Promise<StudentByCedula | null> =>
    http
      .get<{ data: StudentByCedula | null }>(`/students/by-cedula/${cedula}`)
      .then((r) => r.data),

  create: (data: CreateStudentInput): Promise<StudentDetail> =>
    http.post<{ data: StudentDetail }>('/students', data).then((r) => r.data),

  // PATCH (not PUT). Does NOT manage guardians — use studentGuardianApi.
  update: (id: string, data: UpdateStudentInput): Promise<StudentDetail> =>
    http.patch<{ data: StudentDetail }>(`/students/${id}`, data).then((r) => r.data),

  delete: (id: string): Promise<void> => http.delete(`/students/${id}`),

  // Billing (datos de facturación) — upsert / clear the 1:1 record.
  upsertBilling: (id: string, data: BillingInfoInput): Promise<BillingInfo> =>
    http.put<{ data: BillingInfo }>(`/students/${id}/billing`, data).then((r) => r.data),

  deleteBilling: (id: string): Promise<void> =>
    http.delete(`/students/${id}/billing`),

  // POST /students/:id/verify-whatsapp — checks the number via the branch's
  // default WhatsApp session and persists the result.
  verifyWhatsapp: (
    id: string,
  ): Promise<{
    verified: boolean
    whatsappCheckedAt: string | null
    whatsappVerifiedAt: string | null
  }> =>
    http
      .post<{
        data: {
          verified: boolean
          whatsappCheckedAt: string | null
          whatsappVerifiedAt: string | null
        }
      }>(`/students/${id}/verify-whatsapp`)
      .then((r) => r.data),
}
